/**
 * The six cameras of a reflection probe capture (`renderer_scene_cull.cpp:3666-3711`), and the
 * capture each one draws, which the scene's observers read to draw a capture as Godot does.
 */
import * as THREE from 'three';
import { CAPTURE_NEAR, FACE_NORMALS } from '../../godot/rendering/environment/reflectionProbe.js';
/**
 * Each face's up, beside its view direction in `FACE_NORMALS`. They match three's cube cameras, so
 * a face lands in its own face of a cube render target.
 */
const FACE_UPS = [
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(0, 0, -1),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, -1, 0),
];
/** Written only by `captureCameras`. A camera leaves it once it is collected. */
// eslint-disable-next-line no-restricted-syntax -- captureCameras writes each entry from outside, and probeCaptureOf only reads it
const captures = new WeakMap();
/** The capture `camera` draws, or undefined for a camera that draws none. */
export function probeCaptureOf(camera) {
    return captures.get(camera);
}
/** Scratch for one frame or one face's pose, overwritten by each. */
const eye = new THREE.Vector3();
const target = new THREE.Vector3();
const position = new THREE.Vector3();
const rotation = new THREE.Quaternion();
const scale = new THREE.Vector3();
/** The probe's frame: its world transform without the scale, which the probe disables (`reflection_probe.cpp:304-308`). */
export function probeFrame(probeWorld) {
    probeWorld.decompose(position, rotation, scale);
    return new THREE.Matrix4().compose(position, rotation, scale.set(1, 1, 1));
}
/**
 * The cameras of a capture from `originOffset` in the probe's frame, each 90 degrees square from
 * Godot's near plane to its own far plane.
 */
export function captureCameras(probeWorld, originOffset, farPlanes, capture) {
    const frame = probeFrame(probeWorld);
    eye.set(originOffset.x, originOffset.y, originOffset.z);
    return FACE_NORMALS.map((normal, face) => {
        const camera = new THREE.PerspectiveCamera(90, 1, CAPTURE_NEAR, farPlanes[face]);
        target.set(eye.x + normal.x, eye.y + normal.y, eye.z + normal.z);
        const local = new THREE.Matrix4().lookAt(eye, target, FACE_UPS[face]).setPosition(eye);
        camera.matrixWorld.multiplyMatrices(frame, local);
        camera.matrixWorld.decompose(camera.position, camera.quaternion, camera.scale);
        camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
        // The render would otherwise compose the matrix again from the pose, losing the exact frame.
        camera.matrixAutoUpdate = false;
        camera.matrixWorldAutoUpdate = false;
        captures.set(camera, capture);
        return camera;
    });
}
//# sourceMappingURL=captureCameras.js.map